import { SelectionOption } from "helpers/models/auth_models";
import { useTranslation } from "react-i18next";
import { string, ref } from "yup";

export function useAuthValidationRules() {
  const { t } = useTranslation();

  const emailValidationSchema = string()
    .matches(
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
      t("messages:ENTER.VALID") + " " + t("EMAIL")
    )
    .max(320, t("messages:MAXIMUM.CHARACTERS") + " " + t("320"))
    .required(t("EMAIL") + " " + t("messages:IS.REQUIRED"));

  const firstNameValidationSchema = string()
    .trim()
    .required(t("FIRST.NAME") + " " + t("messages:IS.REQUIRED"));

  const lastNameValidationSchema = string()
    .trim()
    .required(t("LAST.NAME") + " " + t("messages:IS.REQUIRED"));

  const roleNameValidationSchema = string()
    .trim()
    .required(t("ROLE.NAME") + " " + t("messages:IS.REQUIRED"));

  const cloneRoleGuidValidationSchema = string().required(
    t("messages:SELECT.OPTION")
  );
  const passwordValidationSchema = string()
    .min(8, t("messages:PWD.MINIMUM.CHARACTERS"))
    .max(50, t("messages:PWD.MAXIMUM.CHARACTERS"))
    .required(t("PASSWORD") + " " + t("messages:IS.REQUIRED"));

  const confirmPasswordValidationSchema = string()
    .min(8, t("messages:PWD.MINIMUM.CHARACTERS"))
    .max(50, t("messages:PWD.MAXIMUM.CHARACTERS"))
    .required(t("CONFIRM.PASSWORD") + " " + t("messages:IS.REQUIRED"))
    .oneOf([ref("password")], t("messages:CONFIRM.PASSWORD.MATCH"));

  const nameValidationSchema = string()
    .trim()
    .required(t("Name") + " " + t("messages:IS.REQUIRED"))
    .max(50, t("messages:MAX.CHARACTERS") + " " + t("50"));

  const companyValidationSchema = string()
    .trim()
    .required(t("Company name") + " " + t("messages:IS.REQUIRED"))
    .matches(
      /^[A-Za-z0-9][A-Za-z0-9 _-]*$/,
      t(
        "Company Name should start with letter or Number and must only contain ' '(Space), '-' and '_'"
      )
    )
    .max(50, t("messages:MAX.CHARACTERS") + " " + t("50"));

  const contactNumberValidationSchema = string()
    .trim()
    .matches(/^\+?\d+$/, t("messages:CONTACT.NUMBER"))
    .max(15, t("messages:MAX.CHARACTERS") + " " + t("15"))
    .min(10, t("messages:MIN.CHARACTERS") + " " + t("10"))
    .required(t("CONTACT.NUMBER") + " " + t("messages:IS.REQUIRED"));

  const address1ValidationSchema = string()
    .trim()
    .required(t("ADDRESS1") + " " + t("messages:IS.REQUIRED"))
    .max(300, t("messages:MAXIMUM.CHARACTERS") + " " + t("300"));

  const address2ValidationSchema = string().max(
    300,
    t("messages:MAXIMUM.CHARACTERS") + " " + t("300")
  );

  const pinCodeValidationSchema = string()
    .trim()
    .required(t("PIN.CODE") + " " + t("messages:IS.REQUIRED"))
    .max(12, t("messages:MAXIMUM.CHARACTERS") + " " + t("12"));

  const websiteUrlValidationSchema = string().max(
    1024,
    t("messages:MAXIMUM.CHARACTERS") + " " + t("1024")
  );

  return {
    emailValidationSchema,
    passwordValidationSchema,
    firstNameValidationSchema,
    lastNameValidationSchema,
    confirmPasswordValidationSchema,
    nameValidationSchema,
    contactNumberValidationSchema,
    address1ValidationSchema,
    address2ValidationSchema,
    pinCodeValidationSchema,
    websiteUrlValidationSchema,
    roleNameValidationSchema,
    cloneRoleGuidValidationSchema,
    companyValidationSchema,
  };
}

export function useDropdownValidationRules(Options: SelectionOption[]) {
  const { t } = useTranslation();
  const dropdownValidationSchema = string()
    .required(t("messages:SELECT.OPTION"))
    .test(t("message:IS.VALID.OPTION"), t("messages:SELECT.VALID"), (value) =>
      Options.some((option) => option.value === value)
    );
  return dropdownValidationSchema;
}
